import React from 'react'
import PropTypes from 'prop-types'
import { Flex, Box, Hide, Text } from 'pcln-design-system'
import moment from 'moment'
import Accordion from '@pcln/accordion'
import SliceDetails from '../../SliceDetails'
import SliceTitle from './SliceTitle'
import FareBrandBox from '../../FareBrandBox'
import { getArrivalDateText, getRetailItineraryTitle } from './helpers'
import { MemberSignIn } from './MemberSignIn'
import { AffirmFooterMessage } from './Affirm/AffirmFooterMessage'
import { Check } from 'pcln-icons'
import { ItineraryFooter } from '../../ItineraryFooter/ItineraryFooter'
import {
  Wrapper,
  MobileHeader,
  SliceDetailsWrapper,
  CreditAppliedStamp,
  TrayButtonWrapper,
  CursorStyles,
  ShowHideAddonsLinks,
} from './RetailItinerary.styles'
import { UpsellTrayContainer } from './UpsellTray/UpsellTrayContainer'
import { useUpsellTray } from './UpsellTray/useUpsellTray'

const RetailItinerary = ({
  airline,
  affirmNode,
  ancillaries,
  arrival,
  fareBrandName,
  crossOutText,
  departing,
  isOvernight,
  label,
  layoverInfo,
  showMemberSignIn,
  signInOnClick,
  onClick,
  operatedByText,
  price,
  seatsAvailable,
  fareBrandBoxTexts,
  duration,
  className,
  altDateText,
  testId,
  airlineTestId,
  hasEuropeFlight,
  cancellationText,
  hideStops,
  isCancellable,
  itinerary,
  isExpoToggleLD,
  showAirportToolTip,
  isNoChangeFee,
  voidWindowInfo,
  searchRequestCabinClass,
  hideAncillaryLegend,
  isReshopExperience,
  reshopPerPersonText,
  tripType,
  isUpsellTray,
  gaTagUpsellTileClick,
  fetchUpsellTrayOptions,
  onListingItemSelect,
  isMultipleAirlinesLogoVariant,
  multipleAirlineText,
  errorLogUpsellCallListings,
}) => {
  const {
    isUpsellTrayOpen,
    isLoadingUpsells,
    isErrorLoadingUpsells,
    upsells,
    onShowAddons,
    onHideAddons,
    handleUpsellOptionClick,
  } = useUpsellTray({
    itinerary,
    fetchUpsellTrayOptions,
    onListingItemSelect,
    errorLogUpsellCallListings,
  })

  const title = getRetailItineraryTitle(layoverInfo)
  const memberSignIn = showMemberSignIn && <MemberSignIn signInOnClick={signInOnClick} />

  const currentDate = moment(Date.now()).format('ddd, MMM D')
  let hideCancellationText = false

  if (departing?.dateRange === currentDate && tripType !== 'MD') {
    hideCancellationText = true
  }

  if (airline && airline.code === 'B6' && hasEuropeFlight && ancillaries.length === 0) {
    ancillaries.push({
      description: 'Carry-on bag',
      inclusion: 'INCLUDED',
      name: 'CARRY_ON',
    })
  }

  const MerchandisingDesktop = ({ label }) => (
    <Hide xs sm>
      <Flex pb={2}>{label}</Flex>
    </Hide>
  )

  const cancellationCopy = cancellationText
    ? cancellationText({
        flightCode: airline?.code,
        isExpoToggleLD,
        arrival,
        departure: departing,
        airlineName: airline?.name,
        voidWindowInfo,
      })
    : null

  const noChangeFeesCopy = 'No change fees'

  let footerString = isNoChangeFee ? ' · No change fees' : ''

  if (hideAncillaryLegend) {
    ancillaries?.forEach((ancillary) => {
      if (ancillary.description === 'Advance seat selection' && ancillary.inclusion === 'INCLUDED') {
        footerString += ' · Seat selection available'
      }
      if (ancillary.description === 'Carry-on bag' && ancillary.inclusion === 'INCLUDED') {
        footerString += ' · Carry-on bag included'
      }
      if (ancillary.description === 'Checked Bags' && ancillary.inclusion === 'INCLUDED') {
        footerString += ' · Checked bags included'
      }
    })
  }

  const greenCheck = <Check size={16} mr={1} color={'success.base'} />

  const footerText = (text) => (
    <Text.span mr={2} fontSize={0}>
      {text}
      {hideAncillaryLegend && footerString}
    </Text.span>
  )

  const isHideFareBrand = searchRequestCabinClass === 'ECO' && fareBrandName.toUpperCase() !== 'MIXED CABIN'

  const creditAppliedStamp = (
    <CreditAppliedStamp
      color='secondary.dark'
      bg='highlight.light'
      borderRadius='full'
      px='12px'
      fontSize='10px'
    >
      Credit Applied
    </CreditAppliedStamp>
  )

  const priceColor = 'success.base'

  return (
    <Wrapper boxShadowSize='sm' className={className} data-testid={testId} borderRadius='xl'>
      <Accordion open={isUpsellTrayOpen}>
        <CursorStyles onClick={onClick} onKeyDown={onClick} tabIndex={0}>
          {(label?.length > 0 || showMemberSignIn || isReshopExperience) && (
            <MobileHeader pl={2} pb={2} pr={2} alignItems='center'>
              {isReshopExperience ? creditAppliedStamp : label}
              <Box ml='auto'>{memberSignIn}</Box>
            </MobileHeader>
          )}

          <Flex alignItems={'stretch'}>
            <SliceDetailsWrapper width='75%' p={2}>
              {label?.length > 0 && <MerchandisingDesktop label={label} />}
              <SliceTitle
                showAirportToolTip={showAirportToolTip}
                isOvernight={isOvernight}
                duration={duration}
                title={title}
                arrivalDateText={getArrivalDateText(
                  arrival?.overnightDays,
                  itinerary?.slices?.[0]?.arrivalInfo?.time?.dateTime
                )}
              />
              <SliceDetails
                departing={departing}
                arrival={arrival}
                layoverInfo={layoverInfo}
                airline={airline}
                operatedByText={operatedByText}
                altDateText={altDateText}
                airlineTestId={airlineTestId}
                hideStops={hideStops}
                showAirportToolTip={showAirportToolTip}
                isMultipleAirlinesLogoVariant={isMultipleAirlinesLogoVariant}
                multipleAirlineText={multipleAirlineText}
              />
            </SliceDetailsWrapper>

            <FareBrandBox
              affirmNode={affirmNode}
              fareBrandName={!isHideFareBrand || isReshopExperience ? fareBrandName : ''}
              price={price}
              ancillaries={ancillaries}
              memberSignIn={memberSignIn}
              crossOutText={crossOutText}
              seatsAvailable={seatsAvailable}
              subPriceTexts={fareBrandBoxTexts}
              priceColor={priceColor}
              isReshopExperience={isReshopExperience}
              reshopPerPersonText={reshopPerPersonText}
              creditAppliedStamp={creditAppliedStamp}
              isUpsellTray={isUpsellTray}
            />
          </Flex>

          {!isReshopExperience && (
            <Flex>
              {(isCancellable || isNoChangeFee) && !hideCancellationText && (
                <ItineraryFooter
                  flexDirection={['column', 'row']}
                  alignItems={[null, 'center']}
                  justifyContent={['center', 'flex-start']}
                  width={[1, 1, affirmNode ? '72%' : 1]}
                >
                  {isCancellable && (
                    <Flex>
                      {greenCheck}
                      {footerText(cancellationCopy)}
                    </Flex>
                  )}
                  {isNoChangeFee && !hideAncillaryLegend && (
                    <Flex mt={[1, 0]}>
                      {greenCheck}
                      {footerText(noChangeFeesCopy)}
                    </Flex>
                  )}
                </ItineraryFooter>
              )}

              {/* Desktop Affirm message */}
              <AffirmFooterMessage affirmNode={affirmNode} isUpsellTray={isUpsellTray} />

              {isUpsellTray && (
                <TrayButtonWrapper xs sm md lg width='28%'>
                  <Accordion.Trigger
                    rightIcon
                    size={16}
                    color='primary.base'
                    onOpen={onShowAddons}
                    onClose={onHideAddons}
                  >
                    <ShowHideAddonsLinks bold fontSize={0} color='primary'>
                      {isUpsellTrayOpen ? 'Hide' : 'Show'} add-ons
                    </ShowHideAddonsLinks>
                  </Accordion.Trigger>
                </TrayButtonWrapper>
              )}
            </Flex>
          )}
        </CursorStyles>

        {isUpsellTray && (
          <Accordion.Content>
            <UpsellTrayContainer
              upsells={upsells}
              isLoading={isLoadingUpsells}
              error={isErrorLoadingUpsells}
              priceColor={priceColor}
              fareBrandBoxTexts={fareBrandBoxTexts}
              handleUpsellOptionClick={handleUpsellOptionClick}
              gaTagUpsellTileClick={gaTagUpsellTileClick}
              airline={airline}
            />
          </Accordion.Content>
        )}
      </Accordion>
    </Wrapper>
  )
}

RetailItinerary.defaultProps = {
  hideStops: false,
  affirmNode: null,
  isNoChangeFee: false,
}

RetailItinerary.propTypes = {
  /** Airline data for logo and airline name */
  airline: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
    code: PropTypes.string,
  }),

  /** Affirm node */
  affirmNode: PropTypes.node,

  /** List of ancillaries */
  ancillaries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      inclusion: PropTypes.string,
      size: PropTypes.number,
      desktopSize: PropTypes.number,
    })
  ),

  /** List of upsell fareBrands */
  upsellFareBrands: PropTypes.arrayOf(
    PropTypes.shape({
      brandId: PropTypes.string,
      sliceKey: PropTypes.string,
      token: PropTypes.string,
      totalFare: PropTypes.number,
      brand: PropTypes.object,
    })
  ),

  /** Arrival data containing time and airport */
  arrival: PropTypes.shape({
    time: PropTypes.string,
    airport: PropTypes.string,
    overnightDays: PropTypes.number,
    isAltAirport: PropTypes.bool,
  }),

  /** Fare brand name string */
  fareBrandName: PropTypes.string,

  /** Crossout price text */
  crossOutText: PropTypes.string,

  /** Departing data containing time and airport */
  departing: PropTypes.shape({
    time: PropTypes.string,
    airport: PropTypes.string,
    isAltAirport: PropTypes.bool,
    dateRange: PropTypes.string,
  }),

  /** Boolean to display the overnight icon */
  isOvernight: PropTypes.bool,

  /** Label node for stuff like "Early Bird" */
  label: PropTypes.array,

  /** List of layover durations and locations  */
  layoverInfo: PropTypes.arrayOf(PropTypes.object),

  /** Member sign in flag */
  showMemberSignIn: PropTypes.bool,

  /** Member sign in onclick callback */
  signInOnClick: PropTypes.func,

  /** Function called when card clicked */
  onClick: PropTypes.func,

  /** Operated by text if airline is operated by another airline */
  operatedByText: PropTypes.string,

  /** Price object with dollars and cents */
  price: PropTypes.object,

  /** Number of seats available */
  seatsAvailable: PropTypes.number,

  /** texts relating to the fare brand box */
  fareBrandBoxTexts: PropTypes.arrayOf(PropTypes.string),

  /** Duration string to display at top */
  duration: PropTypes.string,

  /** Class name(s) to pass in styling changes from parent */
  className: PropTypes.string,

  /** Alt Date string to display alt date above time */
  altDateText: PropTypes.string,

  /** Function for selecting upsell */
  onListingItemSelect: PropTypes.func,

  /** Test identifier for entire component */
  testId: PropTypes.string,

  /** Bool for determining if mobile user */
  isMobile: PropTypes.bool,

  /** Test identifier for airline */
  airlineTestId: PropTypes.string,

  /** Cancellation message used in CancellationFooter */
  cancellationText: PropTypes.func,

  /** Flag used in determining if flight has European destination */
  hasEuropeFlight: PropTypes.bool,

  /** Hide stops at smaller breakpoints */
  hideStops: PropTypes.bool,

  /** Whether or not itinerary is cancellable as per itinerary.voidWindowInfo */
  isCancellable: PropTypes.bool,

  /** Tab ID for determining when to display DL Upsell Modal */
  activeTabId: PropTypes.number,

  /** TripType for determining when to display DL Upsell Modal */
  tripType: PropTypes.string,

  /** Itinerary for callback function when clicking DL Upsell Modal*/
  itinerary: PropTypes.object,

  /** LD toggle to show 'expo 2020 text' - Etihad and Emirates*/
  isExpoToggleLD: PropTypes.bool,

  /** SETI for airport tooltip on hover*/
  showAirportToolTip: PropTypes.bool,

  isNoChangeFee: PropTypes.bool,

  voidWindowInfo: PropTypes.object,

  searchRequestCabinClass: PropTypes.string,

  /** UI changes for rebook/reshop experience */
  isReshopExperience: PropTypes.bool,

  /** Per person copy for rebook/reshop experience */
  reshopPerPersonText: PropTypes.string,

  pclnExpt: PropTypes.object,

  hideAncillaryLegend: PropTypes.bool,

  /** Bottom tray with upsell options on listings */
  isUpsellTray: PropTypes.bool,

  gaTagUpsellTileClick: PropTypes.func,

  fetchUpsellTrayOptions: PropTypes.func,

  isMultipleAirlinesLogoVariant: PropTypes.bool,

  multipleAirlineText: PropTypes.string,

  errorLogUpsellCallListings: PropTypes.func,
}

export default RetailItinerary
